import { vec2, mat2d, glMatrix } from "gl-matrix";
import { TransformTypes, Positions } from "../../constants/TransformerConstants";
import { rotateMatrixAroundPoint, getRadiansFromAngle } from "../math/Angles";

/**
 * @typedef { import("./types").mat2d } mat2d
 * @typedef { import("./types").tool } tool
 * @typedef { import("./types").action } action
 * @typedef { import("./types").Target } target
 * @typedef { import("./types").Bounds } bounds
 * @typedef { import("./types").Mode } mode
 */

glMatrix.setMatrixArrayType(Array);

/**
 * Calculates a new tool object based on the targets, container bounds, and possibly the previous tool.
 * @param {Map<String, targets>} targets
 * @param {tool} prevTool
 * @param {bounds} containerBounds
 * @returns {tool}
 */

export const calculateBounds = (targets, prevTool, canvasBounds, preserved = false) => {
  const tool = { ...prevTool };
  const bounds = { x: 0, y: 0, r: 0, b: 0, w: 0, h: 0, a: 0 };

  if (Object.keys(targets).length === 1) {
    const { x, y, w, h, a } = Object.values(targets)[0];
    bounds.x = x;
    bounds.y = y;
    bounds.r = x + w;
    bounds.b = y + h;
    bounds.a = a ?? 0;
  } else {
    Object.values(targets).forEach(({ bounds: { bottom, left, right, top } }, idx) => {
      if (idx === 0) {
        bounds.x = left;
        bounds.y = top;
        bounds.r = right;
        bounds.b = bottom;
      } else {
        bounds.x = Math.min(left, bounds.x);
        bounds.y = Math.min(top, bounds.y);
        bounds.r = Math.max(bounds.r, right);
        bounds.b = Math.max(bounds.b, bottom);
      }
    });
  }

  if (!preserved) {
    tool.w = Math.abs(bounds.r - bounds.x);
    tool.h = Math.abs(bounds.b - bounds.y);
    tool.x = bounds.x - canvasBounds.left;
    tool.y = bounds.y - canvasBounds.top;
    tool.a = Object.keys(targets).length === 1 ? bounds.a : 0;
  }

  return tool;
};

/**
 * Creates the tool matrix based on the tool's x/y position and angle.
 * @param {Pick<object, "x" | "y" |"w" |"h" | "a">} tool - The tool to update the matrix from.
 * @returns {mat2d} - The generated matrix.
 */

export const updateToolMatrix = (tool) => {
  const matrix = mat2d.create();
  mat2d.translate(matrix, matrix, vec2.fromValues(tool.x, tool.y));
  rotateMatrixAroundPoint(matrix, tool.w / 2, tool.h / 2, getRadiansFromAngle(tool.a));
  return matrix;
};

/**
 * Returns the control and registration point based on the action type and handle used.
 * @param {action} action
 * @param {{
 *  canvasBounds: DOMRect,
 *  containerBounds: DOMRect
 * }} bounds
 * @returns {Object} - An object containing the control and registration point.
 * @property {vec2} ControlPoint - The control point.
 * @property {vec2} RegistrationPoint - The registration point.
 */

export const getRegistrationPoint = (action, bounds) => {
  const { handle, toolStart } = action;
  const { containerBounds, canvasBounds } = bounds;

  const regPoint = vec2.create();
  const controlPoint = vec2.create();

  switch (action.type) {
    case TransformTypes.ROTATE:
      // transform regPoint from element to container coords
      // set to top left of canvases, then to top left of tool, then center of tool
      vec2.set(
        regPoint,
        canvasBounds.left - containerBounds.left,
        canvasBounds.top - containerBounds.top
      );
      vec2.add(regPoint, regPoint, [toolStart.x, toolStart.y]);
      vec2.add(regPoint, regPoint, [toolStart.w / 2, toolStart.h / 2]);
      break;
    case TransformTypes.RESIZE:
      switch (handle) {
        case Positions.TOP_LEFT:
          vec2.set(regPoint, toolStart.w, toolStart.h);
          vec2.set(controlPoint, 0, 0);
          break;
        case Positions.TOP_RIGHT:
          vec2.set(controlPoint, toolStart.w, 0);
          vec2.set(regPoint, 0, toolStart.h);
          break;
        case Positions.TOP_MIDDLE:
          vec2.set(controlPoint, toolStart.w / 2, 0);
          vec2.set(regPoint, toolStart.w / 2, toolStart.h);
          break;
        case Positions.LEFT_MIDDLE:
          vec2.set(controlPoint, 0, toolStart.h / 2);
          vec2.set(regPoint, toolStart.w, toolStart.h / 2);
          break;
        case Positions.RIGHT_MIDDLE:
          vec2.set(controlPoint, toolStart.w, toolStart.h / 2);
          vec2.set(regPoint, 0, toolStart.h / 2);
          break;
        case Positions.BOTTOM_MIDDLE:
          vec2.set(controlPoint, toolStart.w / 2, toolStart.h);
          vec2.set(regPoint, toolStart.w / 2, 0);
          break;
        case Positions.BOTTOM_LEFT:
          vec2.set(regPoint, toolStart.w, 0);
          vec2.set(controlPoint, 0, toolStart.h);
          break;
        case Positions.BOTTOM_RIGHT:
          vec2.set(regPoint, 0, 0);
          vec2.set(controlPoint, toolStart.w, toolStart.h);
          break;
        default:
      }
      break;
    default:
  }
  return { ...action, controlPoint, regPoint };
};
